import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import {
  useRecordOfferDecision,
  type OfferDecisionChannel,
  type WorkOrderOffer,
  type WorkOrderOfferLineDecision,
} from '@/hooks/useWorkOrderOffers';
import { useNotify } from '@/stores/notificationStore';
import { apiErrorCode, describeApiError } from '@/lib/describeApiError';
import { formatCentsForDisplay } from '@/utils/money';
import { lineGrossCents, toDateTimeLocalValue } from './offerDisplay';

const DECISION_ERROR_CODES = [
  'OFFER_CHANGED',
  'OFFER_NOT_OPEN',
  'OFFER_LINES_ALREADY_DECIDED',
  'OFFER_ALREADY_PENDING',
] as const;

const CHANNELS: OfferDecisionChannel[] = [
  'phone',
  'in_person',
  'email',
  'sms',
  'whatsapp',
  'other',
];

interface RecordOfferDecisionDialogProps {
  open: boolean;
  workOrderId: string;
  offer: WorkOrderOffer;
  onClose: () => void;
}

function pendingLines(offer: WorkOrderOffer) {
  return offer.lines.filter((line) => line.decision === 'pending');
}

export function RecordOfferDecisionDialog({
  open,
  workOrderId,
  offer,
  onClose,
}: RecordOfferDecisionDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const recordDecision = useRecordOfferDecision();

  const lines = useMemo(() => pendingLines(offer), [offer]);

  const [lineDecisions, setLineDecisions] = useState<Record<string, 'approved' | 'declined'>>({});
  const [channel, setChannel] = useState<OfferDecisionChannel>('phone');
  const [customerName, setCustomerName] = useState('');
  const [statement, setStatement] = useState('');
  const [reference, setReference] = useState('');
  const [receivedAt, setReceivedAt] = useState(() => toDateTimeLocalValue(new Date()));
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [nameError, setNameError] = useState(false);
  const [statementError, setStatementError] = useState(false);
  const [timeError, setTimeError] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const initial: Record<string, 'approved' | 'declined'> = {};
    for (const line of offer.lines) {
      if (line.decision === 'pending') initial[line.id] = 'approved';
    }
    setLineDecisions(initial);
    setChannel('phone');
    setCustomerName('');
    setStatement('');
    setReference('');
    setReceivedAt(toDateTimeLocalValue(new Date()));
    setIdempotencyKey(crypto.randomUUID());
    setNameError(false);
    setStatementError(false);
    setTimeError(false);
    setSubmitError(null);
  }, [open, offer]);

  const setAll = (decision: 'approved' | 'declined') => {
    setLineDecisions((prev) => {
      const next: Record<string, 'approved' | 'declined'> = {};
      for (const id of Object.keys(prev)) next[id] = decision;
      return next;
    });
  };

  const handleSubmit = async () => {
    const name = customerName.trim();
    const text = statement.trim();
    setNameError(name.length === 0);
    setStatementError(text.length === 0);
    const parsed = new Date(receivedAt);
    const invalidTime = Number.isNaN(parsed.getTime());
    setTimeError(invalidTime);
    const snapshotHash = offer.snapshotHash;
    if (name.length === 0 || text.length === 0 || invalidTime || !snapshotHash) return;

    const decisions = lines.map((line) => ({
      lineId: line.id,
      decision: lineDecisions[line.id] ?? 'approved',
    }));

    const customerApproval: {
      statement: string;
      customerName: string;
      channel: OfferDecisionChannel;
      receivedAt: string;
      reference?: string;
    } = {
      statement: text,
      customerName: name,
      channel,
      receivedAt: parsed.toISOString(),
    };
    const trimmedReference = reference.trim();
    if (trimmedReference) customerApproval.reference = trimmedReference;

    setSubmitError(null);
    try {
      await recordDecision.mutateAsync({
        workOrderId,
        offerId: offer.id,
        data: {
          decisions,
          channel,
          customerApproval,
          expectedSnapshotHash: snapshotHash,
          idempotencyKey,
        },
      });
      notify.success(t('offers.decisionSavedTitle'), t('offers.decisionSavedDesc'));
      onClose();
    } catch (error) {
      const code = apiErrorCode(error);
      const mapped =
        code && (DECISION_ERROR_CODES as readonly string[]).includes(code)
          ? t(`offers.errorCode.${code}`)
          : describeApiError(error, t, {
              codePrefix: 'offers.errorCode',
              fallback: t('offers.decisionFailed'),
            });
      setSubmitError(mapped);
    }
  };

  const decisionLabel = (decision: WorkOrderOfferLineDecision) => t(`offers.lineDecision.${decision}`);

  return (
    <ResponsiveDialog
      open={open}
      onClose={recordDecision.isPending ? undefined : onClose}
      mobileTitle={t('offers.decisionTitle')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('offers.decisionTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {submitError && (
            <Alert severity="error" data-testid="offer-decision-error">
              {submitError}
            </Alert>
          )}
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setAll('approved')}>
              {t('offers.approveAll')}
            </Button>
            <Button size="small" onClick={() => setAll('declined')}>
              {t('offers.declineAll')}
            </Button>
          </Stack>
          {lines.map((line) => (
            <Stack
              key={line.id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="body2">
                {line.description} · {formatCentsForDisplay(lineGrossCents(line))}
              </Typography>
              <RadioGroup
                row
                value={lineDecisions[line.id] ?? 'approved'}
                onChange={(event) => {
                  const value = event.target.value === 'declined' ? 'declined' : 'approved';
                  setLineDecisions((prev) => ({ ...prev, [line.id]: value }));
                }}
                aria-label={t('offers.lineDecisionGroup', { description: line.description })}
              >
                <FormControlLabel
                  value="approved"
                  control={<Radio size="small" />}
                  label={decisionLabel('approved')}
                />
                <FormControlLabel
                  value="declined"
                  control={<Radio size="small" />}
                  label={decisionLabel('declined')}
                />
              </RadioGroup>
            </Stack>
          ))}
          <TextField
            label={t('offers.customerName')}
            value={customerName}
            onChange={(event) => {
              setCustomerName(event.target.value);
              setNameError(false);
            }}
            error={nameError}
            helperText={nameError ? t('offers.customerNameRequired') : undefined}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="offer-decision-channel-label">{t('offers.decisionChannel')}</InputLabel>
            <Select
              labelId="offer-decision-channel-label"
              label={t('offers.decisionChannel')}
              value={channel}
              onChange={(event) => setChannel(event.target.value as OfferDecisionChannel)}
            >
              {CHANNELS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`offers.channel.${value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('offers.receivedAt')}
            type="datetime-local"
            value={receivedAt}
            onChange={(event) => {
              setReceivedAt(event.target.value);
              setTimeError(false);
            }}
            error={timeError}
            helperText={timeError ? t('offers.receivedAtRequired') : undefined}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('offers.statement')}
            value={statement}
            onChange={(event) => {
              setStatement(event.target.value);
              setStatementError(false);
            }}
            error={statementError}
            helperText={statementError ? t('offers.statementRequired') : t('offers.statementHint')}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            label={t('offers.reference')}
            value={reference}
            onChange={(event) => {
              setReference(event.target.value);
            }}
            fullWidth
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={recordDecision.isPending}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={recordDecision.isPending || !offer.snapshotHash}
          startIcon={recordDecision.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {t('offers.decisionSubmit')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
